import React from "react";

/**
 * Geometric shapes inspired by editorial consulting brands (Elenem-style):
 * arcs, dots, thin rules, offset frames — teal / burnt orange / gold.
 */

export function ArcQuarter({ size = 120, color = "gold", stroke = 2, className = "" }) {
    const map = {
        gold: "rgb(184,148,74)",
        orange: "rgb(194,87,54)",
        teal: "rgb(18,59,74)",
    };
    const c = map[color] || color;
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 100 100"
            className={className}
            aria-hidden="true"
        >
            <path
                d="M 100 100 A 100 100 0 0 0 0 0"
                fill="none"
                stroke={c}
                strokeWidth={stroke}
            />
        </svg>
    );
}

/**
 * LinkedCircles — two overlapping outlined circles.
 * Reads as connection, community, "over" — inspired by Elenem-style mark.
 */
export function LinkedCircles({ size = 90, color = "gold", stroke = 1.5, className = "" }) {
    const map = {
        gold: "rgb(184,148,74)",
        orange: "rgb(194,87,54)",
        teal: "rgb(18,59,74)",
    };
    const c = map[color] || color;
    return (
        <svg
            width={size}
            height={size * 0.6}
            viewBox="0 0 100 60"
            className={className}
            aria-hidden="true"
        >
            <circle cx="30" cy="30" r="28" fill="none" stroke={c} strokeWidth={stroke} />
            <circle cx="70" cy="30" r="28" fill="none" stroke={c} strokeWidth={stroke} />
        </svg>
    );
}

/**
 * ChevronMark — a thin double chevron pointing forward. Reads as "advance / next".
 */
export function ChevronMark({ size = 40, color = "orange", stroke = 1.5, className = "" }) {
    const map = {
        gold: "rgb(184,148,74)",
        orange: "rgb(194,87,54)",
        teal: "rgb(18,59,74)",
    };
    const c = map[color] || color;
    return (
        <svg width={size} height={size} viewBox="0 0 40 40" className={className} aria-hidden="true">
            <polyline points="8,10 20,20 8,30" fill="none" stroke={c} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="20,10 32,20 20,30" fill="none" stroke={c} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export function DotGrid({ rows = 5, cols = 5, gap = 14, size = 3, color = "gold", className = "" }) {
    const map = {
        gold: "rgb(184,148,74)",
        orange: "rgb(194,87,54)",
        teal: "rgb(18,59,74)",
        ink: "rgb(26,37,48)",
    };
    const c = map[color] || color;
    const dots = [];
    for (let r = 0; r < rows; r++) {
        for (let col = 0; col < cols; col++) {
            dots.push(
                <circle
                    key={`${r}-${col}`}
                    cx={col * gap + size}
                    cy={r * gap + size}
                    r={size / 2}
                    fill={c}
                />
            );
        }
    }
    const w = (cols - 1) * gap + size * 2;
    const h = (rows - 1) * gap + size * 2;
    return (
        <svg width={w} height={h} className={className} aria-hidden="true">
            {dots}
        </svg>
    );
}

export function CircleOutline({ size = 90, color = "orange", stroke = 2, className = "" }) {
    const map = {
        gold: "rgb(184,148,74)",
        orange: "rgb(194,87,54)",
        teal: "rgb(18,59,74)",
    };
    const c = map[color] || color;
    return (
        <svg width={size} height={size} viewBox="0 0 100 100" className={className} aria-hidden="true">
            <circle cx="50" cy="50" r={50 - stroke} fill="none" stroke={c} strokeWidth={stroke} />
        </svg>
    );
}

export function HairlineCross({ size = 24, color = "gold", className = "" }) {
    const map = {
        gold: "rgb(184,148,74)",
        orange: "rgb(194,87,54)",
        teal: "rgb(18,59,74)",
    };
    const c = map[color] || color;
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
            <line x1="12" y1="0" x2="12" y2="24" stroke={c} strokeWidth="1" />
            <line x1="0" y1="12" x2="24" y2="12" stroke={c} strokeWidth="1" />
        </svg>
    );
}

/** A composed geometric flourish suitable for hero corners */
export function HeroMarks({ className = "" }) {
    return (
        <div className={`pointer-events-none select-none ${className}`}>
            <div className="absolute -top-6 -right-6 hidden md:block">
                <ArcQuarter size={140} color="gold" stroke={1.5} />
            </div>
            <div className="absolute -bottom-4 -left-4 hidden md:block">
                <DotGrid rows={4} cols={4} gap={12} size={2.5} color="teal" />
            </div>
        </div>
    );
}
